import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { FaUser, FaCamera } from 'react-icons/fa';
import { FiStar, FiAward } from 'react-icons/fi';
import { supabase } from '@/lib/supabase';
import { userService, User } from '@/services/userService';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from '@/components/Toast/Toast';
import BadgeDisplay from './BadgeDisplay';
import { useQuery } from '@tanstack/react-query';
import { getBadgeForCourse } from './badgeService';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Replace learnApi calls with badgeService
  const { data: courses } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      const { data, error } = await supabase.from('courses').select('*');
      if (error) throw error;
      return data;
    },
  });

  // Fetch user's course progress
  const { data: courseProgress } = useQuery({
    queryKey: ['courseProgress', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      const { data, error } = await supabase
        .from('user_course_progress')
        .select('*')
        .eq('user_id', session.user.id);
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const badgeCourses = useMemo(() => {
    if (!courseProgress || !courses) return [];
    
    return courseProgress.map(progress => {
      const course = courses.find(c => c.id === progress.course_id);
      
      return {
        id: progress.course_id,
        title: course?.title || 'Unknown Course',
        slug: course?.slug || '',
        pathTitle: course?.learning_path_id.toString() || '',
        progress: {
          is_completed: Boolean(progress.completed_at),
          completed_tutorials: progress.completed_tutorials,
          total_tutorials: progress.total_tutorials,
          completion_percentage: progress.total_tutorials
            ? (progress.completed_tutorials / progress.total_tutorials) * 100
            : 0,
        },
        completed_at: progress.completed_at
      };
    });
  }, [courseProgress, courses]);

  const { data: badges } = useQuery({
    queryKey: ['badges', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      
      // Get badges for all completed courses
      const badgePromises = badgeCourses
        .filter(course => course.progress.is_completed)
        .map(course => getBadgeForCourse(course.id.toString(), session.user.id));
      
      return Promise.all(badgePromises);
    },
    enabled: !!session?.user?.id && !!badgeCourses.length,
  });

  useEffect(() => {
    if (session?.user?.id) {
      loadUserProfile();
      const subscription = userService.subscribeToUserChanges(
        session.user.id,
        (payload) => {
          if (payload.new) {
            setUserProfile(payload.new as User);
            setUsername(payload.new.username || '');
          }
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [session?.user?.id]);

  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      const profile = await userService.getUserProfile(session!.user!.id);
      setUserProfile(profile);
      setUsername(profile.username || '');
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(t('errors.failed_to_load_profile'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsLoading(true);
      setError('');
      
      const file = event.target.files?.[0];
      if (!file) return;

      // Upload the image and get the public URL
      const publicUrl = await userService.uploadUserPicture(session!.user!.id, file);

      // Update the user profile with the new avatar URL using RPC
      const { data, error } = await supabase.rpc('update_user_profile', {
        p_username: userProfile?.username || '',
        p_avatar_url: publicUrl,
      });

      if (error) {
        console.error('Update failed:', error);
        throw error;
      }

      console.log('Profile updated successfully!', data);

    } catch (err: any) {
      console.error('Error updating avatar:', err);
      // Show a more specific error message based on the error type
      if (err.message === 'File must be an image') {
        setError(t('errors.invalid_image_type'));
      } else if (err.message === 'File size must be less than 6MB') {
        setError(t('errors.file_too_large'));
      } else {
        setError(t('errors.failed_to_update_avatar'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUsernameUpdate = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase.rpc('update_user_profile', {
        p_username: username,
        p_avatar_url: userProfile?.avatar_url || '',
      });

      if (error) {
        console.error('Update failed:', error);
        throw error;
      }

      console.log('Profile updated successfully!', data);

    } catch (err) {
      console.error('Error updating username:', err);
      setError(t('errors.failed_to_update_username'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!userProfile) {
    return (
      <div className="container mx-auto px-1 sm:px-4 mt-8 sm:mt-16 max-w-[100vw]">
        <div className="card bg-base-200 shadow-xl">
          <div className="card-body p-2 sm:p-6">
            <h2 className="card-title">{t('common.loading')}</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 mt-4 sm:mt-16 max-w-[100vw] pb-6 relative overflow-y-auto">
      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Grid pattern */}
        <img
          src="/images/grid-pattern.svg"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        {/* Circuit pattern overlay */}
        <img
          src="/images/circuit-pattern.svg"
          alt=""
          className="w-full h-full object-cover absolute inset-0 opacity-5"
        />
        
        {/* Animated glowing lines */}
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent animate-[moveLeftRight_8s_linear_infinite]"></div>
        <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent animate-[moveLeftRight_12s_linear_infinite_reverse]"></div>
      </div>

      <div className="relative z-10">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-primary drop-shadow-glow mb-2 mt-8 text-center font-mono">
            {t('profile.title', 'Profile')}
          </h1>
          <div className="w-16 h-1 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
        </div>

        <div className="card bg-base-100/80 backdrop-blur-md border border-primary/20 shadow-lg overflow-hidden relative">
          {/* Top card accent line */}
          <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent"></div>
          
          {/* Animated scanlines effect */}
          <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(138,75,255,0.1)_50%)] bg-[length:100%_4px] animate-scanlines"></div>
          </div>
          
          <div className="card-body p-3 sm:p-6">
            {error && (
              <div className="alert alert-error mb-4 text-xs sm:text-sm">
                <span>{error}</span>
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Avatar Section */}
              <div className="flex flex-col items-center gap-4 md:border-r md:border-primary/10 md:pr-8 relative">
                {/* Decorative circuit line */}
                <div className="absolute top-0 right-0 md:h-full md:w-[1px] w-full h-[1px] bg-gradient-to-b from-primary/10 via-primary/50 to-primary/10 hidden md:block"></div>
                
                <div className="relative group">
                  {/* Avatar glow effect */}
                  <div className="absolute -inset-1 rounded-full bg-primary/30 blur-md opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="avatar relative">
                    <div className="w-28 sm:w-36 rounded-full ring-2 ring-primary/80 ring-offset-base-100 ring-offset-2 shadow-[0_0_15px_rgba(138,75,255,0.5)] group-hover:shadow-[0_0_25px_rgba(138,75,255,0.8)] transition-all duration-300">
                      {userProfile?.avatar_url ? (
                        <img 
                          src={userProfile.avatar_url} 
                          alt={userProfile.username} 
                          className="bg-base-200"
                        />
                      ) : (
                        <div className="bg-gradient-to-br from-base-200 to-base-300 w-full h-full flex items-center justify-center">
                          <FaUser className="w-10 h-10 sm:w-14 sm:h-14 text-primary/60" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <label className="absolute bottom-1 right-1 btn btn-circle btn-sm sm:btn-md bg-primary hover:bg-primary/90 border-none text-base-100 shadow-[0_0_10px_rgba(138,75,255,0.7)]">
                    <FaCamera className="w-3 h-3 sm:w-4 sm:h-4" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      disabled={isLoading}
                    />
                  </label>
                </div>

                {/* Username with cyberpunk styling */}
                <div className="form-control w-full max-w-[250px]">
                  <label className="label">
                    <span className="label-text text-primary font-mono font-bold">{t('profile.username')}</span>
                  </label>
                  <div className="flex gap-2 w-full group/input">
                    <div className="relative flex-1">
                      {/* Input glow effect */}
                      <div className="absolute -inset-0.5 bg-primary/20 rounded-md blur-sm opacity-0 group-hover/input:opacity-100 transition-opacity"></div>
                      
                      <input
                        type="text"
                        placeholder={t('profile.enter_username')}
                        className="input input-bordered input-sm sm:input-md flex-1 min-w-0 bg-base-200/90 border-primary/40 focus:border-primary z-10 relative font-mono placeholder:text-base-content/30"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                    
                    <button
                      className={`btn btn-primary btn-sm sm:btn-md whitespace-nowrap shadow-[0_0_10px_rgba(138,75,255,0.3)] hover:shadow-[0_0_15px_rgba(138,75,255,0.5)] ${isLoading ? 'loading' : ''}`}
                      onClick={handleUsernameUpdate}
                      disabled={isLoading}
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 space-y-6">
                {/* Stats - Cyberpunk style */}
                <div className="relative group">
                  <h3 className="text-sm sm:text-base font-bold text-primary mb-3 font-mono uppercase tracking-wider flex items-center">
                    <span className="inline-block w-4 h-0.5 bg-primary mr-2"></span>
                    {t('profile.your_stats')}
                    <span className="inline-block w-4 h-0.5 bg-primary ml-2"></span>
                  </h3>
                  
                  {/* Stats outer glow */}
                  <div className="absolute -inset-1 bg-primary/20 rounded-box blur-md opacity-40 group-hover:opacity-70 transition-opacity"></div>
                  
                  <div className="stats stats-vertical lg:stats-horizontal w-full bg-base-200/90 text-base-content border border-primary/30 shadow-[0_0_15px_rgba(0,0,0,0.2)] relative">
                    <div className="stat py-2 px-3 sm:p-4 group/stat">
                      <div className="stat-title text-xs sm:text-sm opacity-70 font-mono uppercase">{t('profile.elo_rating')}</div>
                      <div className="stat-value text-lg sm:text-2xl tabular-nums text-primary drop-shadow-glow group-hover/stat:scale-110 transition-transform">{userProfile?.elo_rating || 0}</div>
                    </div>
                    <div className="stat py-2 px-3 sm:p-4 group/stat">
                      <div className="stat-title text-xs sm:text-sm opacity-70 font-mono uppercase">{t('profile.level')}</div>
                      <div className="stat-value text-lg sm:text-2xl tabular-nums group-hover/stat:scale-110 transition-transform">{userProfile?.level || 0}</div>
                    </div>
                    <div className="stat py-2 px-3 sm:p-4 group/stat">
                      <div className="stat-title text-xs sm:text-sm opacity-70 font-mono uppercase">{t('profile.experience')}</div>
                      <div className="stat-value text-lg sm:text-2xl tabular-nums group-hover/stat:scale-110 transition-transform">{userProfile?.experience || 0}</div>
                    </div>
                    <div className="stat py-2 px-3 sm:p-4 group/stat">
                      <div className="stat-title text-xs sm:text-sm opacity-70 font-mono uppercase">{t('profile.weekly_elo')}</div>
                      <div className="stat-value text-lg sm:text-2xl tabular-nums group-hover/stat:scale-110 transition-transform">{userProfile?.weekly_elo || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Subscription Status - Cyberpunk style */}
                <div className="relative group">
                  {/* Subscription card glow */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-warning/30 rounded-box blur-md opacity-40 group-hover:opacity-70 transition-opacity"></div>
                  
                  <div className="card bg-base-200/90 border border-primary/20 shadow-lg relative overflow-hidden">
                    {/* Decorative corner elements */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/70"></div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/70"></div>
                    
                    <div className="card-body p-3 sm:p-5 relative">
                      <h3 className="card-title text-sm sm:text-base flex items-center gap-2 text-warning drop-shadow-glow font-mono uppercase tracking-wide">
                        <FiStar className="text-warning" />
                        {t('profile.subscription_status')}
                      </h3>
                      
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 relative z-10">
                        <div>
                          <p className="text-base sm:text-lg font-semibold text-base-content/90">
                            {userProfile?.is_premium ? 
                              <span className="text-warning font-mono">{t('subscribe.premiumPlan')}</span> : 
                              <span className="font-mono">{t('subscribe.freePlan')}</span>
                            }
                          </p>
                          {userProfile?.premium_until && (
                            <p className="text-xs sm:text-sm opacity-70 font-mono">
                              {t('profile.subscription_ends')}: <span className="text-warning">{new Date(userProfile.premium_until).toLocaleDateString()}</span>
                            </p>
                          )}
                        </div>
                        
                        {!userProfile?.is_premium && (
                          <button
                            className="btn btn-warning btn-sm sm:btn-md gap-2 shadow-[0_0_15px_rgba(251,189,35,0.3)] hover:shadow-[0_0_20px_rgba(251,189,35,0.5)]"
                            onClick={() => navigate('/subscribe')}
                          >
                            <FiStar />
                            {t('profile.upgrade_now')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Badges Section - Enhanced cyberpunk style */}
            <div className="mt-8 relative">
              {/* Outer glow */}
              <div className="absolute -inset-2 bg-primary/30 rounded-box blur-xl opacity-40"></div>
              
              <div className="card bg-base-100/90 backdrop-blur-sm border-2 border-primary/40 shadow-[0_0_25px_rgba(0,0,0,0.2)] relative overflow-hidden">
                {/* Decorative circuit lines */}
                <div className="absolute top-0 left-1/4 w-1/2 h-[1px] bg-gradient-to-r from-transparent via-primary/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
                <div className="absolute top-0 bottom-0 left-[10%] w-[1px] h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent"></div>
                <div className="absolute top-0 bottom-0 right-[10%] w-[1px] h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent"></div>
                
                {/* Scanlines */}
                <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(138,75,255,0.05)_50%)] bg-[length:100%_4px] opacity-30"></div>
                
                <div className="card-body p-4 sm:p-6 relative">
                  <h2 className="font-mono text-base sm:text-lg font-bold uppercase tracking-wider flex items-center gap-3 text-primary drop-shadow-glow mb-4">
                    <FiAward className="text-xl text-primary" />
                    {t('profile.badges')}
                    <span className="flex-1 h-[1px] bg-gradient-to-r from-primary/80 to-transparent"></span>
                  </h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-2">
                    {badgeCourses.map((course) => {
                      const badge = badges?.find(b => b?.name === course.title);
                      return (
                        <BadgeDisplay
                          key={course.id}
                          courseId={course.id.toString()}
                          className="w-full"
                          courseDetails={{
                            is_completed: course.progress.is_completed,
                            progress_percentage: course.progress.completion_percentage,
                            completed_tutorial_count: course.progress.completed_tutorials,
                            title: badge?.name || course.title,
                            path_title: course.pathTitle,
                            last_activity_at: course.completed_at || new Date().toISOString()
                          }}
                        />
                      );
                    })}
                    
                    {badgeCourses.length === 0 && (
                      <div className="col-span-full flex flex-col items-center justify-center py-12 text-sm sm:text-base text-base-content/60">
                        <div className="w-16 h-16 rounded-full bg-base-200/50 flex items-center justify-center mb-4 relative">
                          <div className="absolute inset-0 rounded-full border border-primary/30 animate-pulse"></div>
                          <FiAward className="w-8 h-8 text-primary/40" />
                        </div>
                        <p className="font-mono uppercase tracking-wide">{t('profile.noBadges')}</p>
                        <div className="mt-2 w-32 h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    <br></br>

    </div>
  );
}